import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setActiveTenantId } from '@/api/client';
import { auth as authApi } from '@/api/auth';
import { decodeJwt, isExpired, isPlatformAdmin, type JwtClaims } from './jwt';
import { tokenStore } from './tokenStore';

/**
 * Tracks the current session — bearer token + decoded claims + a derived
 * "portal" flag that the router uses to send a user to /platform vs /tenant.
 *
 * We keep state in React rather than re-decoding on every render so the
 * top-level shell only re-renders when login state actually changes.
 *
 * Listens for the `auth:expired` window event the axios interceptor fires
 * on 401, so any caller that gets a stale-token response auto-resolves to
 * "logged out" without the call site doing anything.
 */

export type Portal = 'platform' | 'tenant';

interface AuthState {
  token: string | null;
  claims: JwtClaims | null;
  portal: Portal | null;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (token: string) => void;
  signOut: () => void;
}

const initialState: AuthState = {
  token: null,
  claims: null,
  portal: null,
  isAuthenticated: false,
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function deriveState(token: string | null): AuthState {
  if (!token) return initialState;
  const claims = decodeJwt(token);
  if (!claims || isExpired(claims)) return initialState;
  const portal: Portal = isPlatformAdmin(claims) ? 'platform' : 'tenant';
  return { token, claims, portal, isAuthenticated: true };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => deriveState(tokenStore.get()));

  // Keep axios's tenant header synced with the active session.
  useEffect(() => {
    setActiveTenantId(state.claims?.tenantId ?? null);
  }, [state.claims?.tenantId]);

  // 401 from any call → clear local state. tokenStore is already cleared
  // by the axios interceptor; we just react to the broadcast.
  useEffect(() => {
    const onExpired = () => setState(initialState);
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  // Cross-tab logout: another tab cleared the token.
  useEffect(() => {
    const unsubscribe = tokenStore.subscribe((token) => setState(deriveState(token)));
    return () => {
      unsubscribe();
    };
  }, []);

  const signIn = useCallback((token: string) => {
    tokenStore.set(token);
    setState(deriveState(token));
  }, []);

  const signOut = useCallback(() => {
    // Best-effort: tell the auth service to invalidate the refresh row
    // so it can't be used elsewhere. Failures are silent — local state
    // is what the user actually sees.
    const refresh = tokenStore.getRefresh();
    if (refresh) {
      void authApi.logout(refresh).catch(() => undefined);
    }
    tokenStore.clear();
    setState(initialState);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut }),
    [state, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
